/**
 * What the AI assistant remembers about the current user, with a way to prune it.
 *
 * Reads GET /chat/memory/ and removes single entries (or everything) via
 * DELETE /chat/memory/. Strictly self-service — the endpoint only ever returns
 * the caller's own memory.
 * @component
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../../services/api';

const CATEGORY_LABELS = {
  preference: 'Preference',
  context: 'Context',
  working_style: 'Working style',
  other: 'Note',
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '';

const trendChip = (word) => {
  if (!word) return null;
  return (
    <Chip
      size="small"
      label={word}
      color={word === 'improving' ? 'success' : 'warning'}
      variant="outlined"
      sx={{ ml: 0.5, height: 18, fontSize: 11 }}
    />
  );
};

const describeStats = (stats = {}) => {
  const trends = stats.trends || {};
  const rows = [
    { label: 'Tasks completed', value: stats.tasks_completed ?? 0, trend: trends.tasks_completed },
    { label: 'Action items completed', value: stats.items_completed ?? 0 },
    { label: 'Overdue', value: stats.tasks_overdue ?? 0, trend: trends.tasks_overdue },
    {
      label: 'Time logged',
      value:
        stats.logging_consistency_pct == null
          ? '—'
          : `${stats.hours_logged ?? 0}h on ${stats.logging_consistency_pct}% of weekdays`,
      trend: trends.logging_consistency_pct,
    },
  ];
  if (stats.estimate_ratio != null) {
    const r = stats.estimate_ratio;
    let text = 'accurate';
    if (r > 1.15) text = `under-estimates (~${Math.round((r - 1) * 100)}% over)`;
    else if (r < 0.85) text = `over-estimates (~${Math.round((1 - r) * 100)}% under)`;
    rows.push({ label: 'Estimates', value: text, trend: trends.estimate_ratio });
  }
  return rows;
};

const AssistantMemorySettings = () => {
  const [memory, setMemory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [toast, setToast] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get('chat/memory/')
      .then((res) => setMemory(res.data))
      .catch(() => setToast({ severity: 'error', message: 'Could not load assistant memory.' }))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const remove = async (payload, successMessage) => {
    setBusy(true);
    try {
      const res = await api.delete('chat/memory/', { data: payload });
      setMemory(res.data);
      setToast({ severity: 'success', message: successMessage });
    } catch (err) {
      setToast({
        severity: 'error',
        message: err?.response?.data?.error || 'Could not update memory.',
      });
    } finally {
      setBusy(false);
      setConfirmAll(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const notes = memory?.notes || [];
  const nicknames = memory?.nicknames || [];
  const snapshots = memory?.snapshots || [];
  const isEmpty = !notes.length && !nicknames.length && !snapshots.length;

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box>
          <Typography variant="h5">Assistant Memory</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Everything the AI assistant knows about you. It uses this to work like your own
            assistant rather than a generic one. Only you can see this page; the assistant never
            shares it with anyone else.
          </Typography>
        </Box>
        {!isEmpty && (
          <Button color="error" variant="outlined" size="small" onClick={() => setConfirmAll(true)} disabled={busy}>
            Forget everything
          </Button>
        )}
      </Box>

      {isEmpty && (
        <Alert severity="info">
          Nothing remembered yet. Tell the assistant how you like to work, and it will build a
          picture of your work patterns at the end of each sprint period.
        </Alert>
      )}

      {/* Learned facts */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Things it has learned about you
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Preferences and context you've told it in chat. Tell the assistant to remember or forget
          something, or remove entries here.
        </Typography>
        {notes.length ? (
          <List dense>
            {notes.map((note) => (
              <ListItem
                key={note.id}
                secondaryAction={
                  <Tooltip title="Forget this">
                    <IconButton edge="end" size="small" disabled={busy} onClick={() => remove({ note_id: note.id }, 'Forgotten.')}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={note.fact}
                  secondary={
                    <>
                      <Chip size="small" label={CATEGORY_LABELS[note.category] || 'Note'} sx={{ mr: 1, height: 18, fontSize: 11 }} />
                      {fmtDate(note.created_at)}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
            No facts yet.
          </Typography>
        )}
      </Paper>

      {/* Nicknames */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Your shorthand
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Names you use for tracks, people and projects, so it never has to ask again.
        </Typography>
        {nicknames.length ? (
          <List dense>
            {nicknames.map((n) => (
              <ListItem
                key={n.alias}
                secondaryAction={
                  <Tooltip title="Forget this">
                    <IconButton edge="end" size="small" disabled={busy} onClick={() => remove({ alias: n.alias }, 'Forgotten.')}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText primary={`“${n.alias}” → ${n.name}`} secondary={n.type} />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
            No shorthand yet.
          </Typography>
        )}
      </Paper>

      {/* Snapshots */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          How you work
        </Typography>
        <Typography variant="body2" color="text.secondary">
          A snapshot written at the end of each sprint period from your tasks, time logs and
          biweekly reports. Newest first.
        </Typography>
        {snapshots.length ? (
          <Stack spacing={2} sx={{ mt: 2 }} divider={<Divider flexItem />}>
            {snapshots.map((snap) => (
              <Box key={snap.id}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">
                    {fmtDate(snap.period_start)} – {fmtDate(snap.period_end)}
                  </Typography>
                  <Tooltip title="Delete this snapshot">
                    <IconButton size="small" disabled={busy} onClick={() => remove({ snapshot_id: snap.id }, 'Snapshot deleted.')}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {snap.summary && (
                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    {snap.summary}
                  </Typography>
                )}
                <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5, mt: 1, mb: 0 }}>
                  {describeStats(snap.stats).map((row) => (
                    <React.Fragment key={row.label}>
                      <Typography component="dt" variant="body2" color="text.secondary">
                        {row.label}
                      </Typography>
                      <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                        {row.value}
                        {trendChip(row.trend)}
                      </Typography>
                    </React.Fragment>
                  ))}
                  {(snap.stats?.top_tracks || []).length > 0 && (
                    <>
                      <Typography component="dt" variant="body2" color="text.secondary">Tracks</Typography>
                      <Typography component="dd" variant="body2" sx={{ m: 0 }}>{snap.stats.top_tracks.join(', ')}</Typography>
                    </>
                  )}
                  {(snap.stats?.collaborators || []).length > 0 && (
                    <>
                      <Typography component="dt" variant="body2" color="text.secondary">Works with</Typography>
                      <Typography component="dd" variant="body2" sx={{ m: 0 }}>{snap.stats.collaborators.join(', ')}</Typography>
                    </>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
            No snapshots yet — the first one is written when your current sprint period ends.
          </Typography>
        )}
      </Paper>

      <Dialog open={confirmAll} onClose={() => setConfirmAll(false)}>
        <DialogTitle>Forget everything?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes every remembered fact, shorthand and work snapshot. The assistant will
            start from scratch with you. This can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAll(false)} disabled={busy}>Cancel</Button>
          <Button color="error" variant="contained" disabled={busy} onClick={() => remove({ all: true }, 'Memory cleared.')}>
            Forget everything
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : null}
      </Snackbar>
    </Stack>
  );
};

export default AssistantMemorySettings;
